import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Lightbox from "../components/Lightbox";
import { Maximize2, Grid3x3, Grid2x2, List } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return "/api/images/hero_culture_baoule.png";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

export default function Gallery() {
  const navigate = useNavigate();
  const [gallery, setGallery] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [viewMode, setViewMode] = useState('grid-4'); // 'grid-4', 'grid-3', 'grid-2', 'list'

  useEffect(() => {
    fetch(`${API}/cms/homepage`)
      .then(res => res.json())
      .then(data => setGallery(data.gallery || []))
      .catch(console.error);
  }, []);

  const openLightbox = (index = 0) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const getGridClass = () => {
    switch (viewMode) {
      case 'grid-2': return 'grid sm:grid-cols-2 gap-6';
      case 'grid-3': return 'grid sm:grid-cols-2 lg:grid-cols-3 gap-6';
      case 'grid-4': return 'grid sm:grid-cols-2 lg:grid-cols-4 gap-4';
      case 'list': return 'grid grid-cols-1 gap-6';
      default: return 'grid sm:grid-cols-2 lg:grid-cols-4 gap-4';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
                📸 Galerie Culturelle
              </h1>
              <p className="text-muted-foreground">
                Découvrez la richesse culturelle baoulé en images
              </p>
            </div>

            {/* View Mode Toggle */}
            <div className="hidden sm:flex items-center gap-2 bg-muted p-1 rounded-lg">
              <button
                onClick={() => setViewMode('grid-4')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid-4' ? 'bg-primary text-white' : 'hover:bg-background'
                }`}
                title="Grille 4 colonnes"
              >
                <Grid3x3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('grid-3')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid-3' ? 'bg-primary text-white' : 'hover:bg-background'
                }`}
                title="Grille 3 colonnes"
              >
                <Grid2x2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' ? 'bg-primary text-white' : 'hover:bg-background'
                }`}
                title="Liste"
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-primary">{gallery.length}</p>
              <p className="text-sm text-muted-foreground">Photos</p>
            </div>
            <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-secondary">12</p>
              <p className="text-sm text-muted-foreground">Collections</p>
            </div>
            <div className="bg-gradient-to-br from-accent/10 to-accent/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-accent">150+</p>
              <p className="text-sm text-muted-foreground">Vues</p>
            </div>
            <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-orange-500">24</p>
              <p className="text-sm text-muted-foreground">Contributeurs</p>
            </div>
          </div>

          {/* Gallery Grid */}
          {gallery.length > 0 ? (
            <div className={getGridClass()}>
              {gallery.map((item, i) => (
                <div 
                  key={item.id || i} 
                  className={`gallery-item animate-slide-up cursor-pointer group ${
                    viewMode === 'list' ? 'flex gap-4 items-center' : ''
                  }`}
                  style={{animationDelay: `${i * 0.05}s`}}
                  onClick={() => openLightbox(i)}
                >
                  <div className={`relative overflow-hidden rounded-xl ${
                    viewMode === 'list' ? 'w-48 h-32' : 'aspect-square'
                  }`}>
                    <img 
                      src={getImageUrl(item.image_url)} 
                      alt={item.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="p-2 bg-white/20 backdrop-blur rounded-full">
                        <Maximize2 className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <div className={viewMode === 'list' ? 'flex-1' : 'gallery-caption'}>
                    <h4 className="font-heading font-bold text-lg">{item.title}</h4>
                    <p className="text-sm opacity-80">{item.subtitle}</p>
                    {viewMode === 'list' && item.description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                <Grid3x3 className="w-12 h-12 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2">
                Aucune photo pour le moment
              </h3>
              <p className="text-muted-foreground mb-4">
                La galerie sera bientôt remplie de magnifiques photos culturelles !
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Lightbox */}
      {lightboxOpen && gallery.length > 0 && (
        <Lightbox 
          images={gallery.map(item => ({
            image_url: item.image_url,
            title: item.title,
            subtitle: item.subtitle
          }))}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
